import { marked } from 'marked';

import { MENTION_TOKEN_PATTERN } from '@/helpers/assignPersonMention';

/** Converts markdown syntax to HTML. Links (bare URLs, emails, markdown syntax) are
 * converted to <a> tags by marked's GFM engine; a post-processing step then adds
 * target="_blank" rel="noopener noreferrer nofollow" to those links.
 * Callers must not run separate URL or email linkification passes on the output. */
const convertMarkdownToHtml = (message: string): string => {
  // Use a random sentinel so placeholders cannot collide with user-typed text.
  const sentinel = Math.random().toString(36).slice(2);
  const tokenMap = new Map<string, string>();
  let tokenCounter = 0;

  const tokenize = (match: string): string => {
    const placeholder = `\x02TEMPTOKEN_${sentinel}_${tokenCounter}\x03`;
    tokenMap.set(placeholder, match);
    tokenCounter += 1;
    return placeholder;
  };

  // Tokenize {{@...}} mention tokens so marked does not process their content.
  const withMentionsReplaced = message.replace(MENTION_TOKEN_PATTERN, tokenize);

  let result: string;
  try {
    const parsed = marked.parseInline(withMentionsReplaced);
    result = typeof parsed === 'string' ? parsed : withMentionsReplaced;
  } catch {
    result = withMentionsReplaced;
  }

  // Add target/rel to any link that doesn't already have a target attribute.
  result = result.replace(/<a\b(?![^>]*\starget\s*=)/gi, '<a target="_blank" rel="noopener noreferrer nofollow"');

  // Normalize rel on any target="_blank" anchor that already existed in the input.
  // Step 1 above only stamps anchors that had no target; this ensures pre-existing
  // _blank anchors also carry the required rel tokens.
  result = result.replace(/<a\b[^>]*\starget\s*=\s*["']_blank["'][^>]*>/gi, (match) => {
    if (/\srel\s*=/i.test(match)) {
      return match.replace(/(\srel\s*=\s*(["']))[^"']*\2/gi, '$1noopener noreferrer nofollow$2');
    }
    return match.replace(/>$/, ' rel="noopener noreferrer nofollow">');
  });

  // marked percent-encodes placeholders that appear inside href attributes (e.g.
  // [text](\x02TEMPTOKEN_...\x03) → <a href="%02TEMPTOKEN_...%03">text</a>), so restore
  // both the raw and percent-encoded forms. Use split/join for global replacement so
  // every occurrence is substituted even if the same placeholder somehow appears more than once.
  tokenMap.forEach((originalToken, placeholder) => {
    result = result.split(placeholder).join(originalToken);
    const encodedPlaceholder = encodeURIComponent(placeholder);
    if (encodedPlaceholder !== placeholder) {
      result = result.split(encodedPlaceholder).join(originalToken);
    }
  });

  return result;
};

export default convertMarkdownToHtml;
